import { useRequest } from 'features/request/useRequest';
import type { PriceListUerRatioRowFilterInput, RatioTypeEnum } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { uerPriceListRatioRowsWithScopesGql } from '../../workOrderQueries';

const requestId = 'uerPriceListRatioRowsWithScopes';

interface IUseUerPriceListRatioRowsWithScopes {
    uerBaseRowId: number;
    ratioType: RatioTypeEnum;
    contractId: number;
}

export const useUerPriceListRatioRowsWithScopes = (props: IUseUerPriceListRatioRowsWithScopes) => {
    const { uerBaseRowId, ratioType, contractId } = props;
    const [filterWhere, setFilterWhere] = useState({});

    const changeFilterHandler = useCallback(
        ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => {
            setFilterWhere((prevState) => ({
                ...prevState,
                [fieldName]: checkedValues?.length ? { in: checkedValues } : undefined,
            }));
        },
        [],
    );

    const variables = useMemo(() => {
        const where: PriceListUerRatioRowFilterInput = {
            and: [
                {
                    ratioType: { eq: ratioType },
                },
                filterWhere,
            ],
        };

        return {
            uerBaseRowId,
            contractId,
            where,
            order: [{ ratioType: SortEnumType.Asc }, { number: SortEnumType.Asc }],
        };
    }, [contractId, filterWhere, ratioType, uerBaseRowId]);

    const [
        uerPriceListRatioRowsWithScopesData,
        uerPriceListRatioRowsWithScopesStatus,
        ,
        uerPriceListRatioRowsWithScopesRequest,
    ] = useRequest({
        requestId,
        query: uerPriceListRatioRowsWithScopesGql,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: uerPriceListRatioRowsWithScopesRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        getRowParams?.current?.successCallback(
            uerPriceListRatioRowsWithScopesData?.uerPriceListRatioRowsWithScopes?.items || [],
            uerPriceListRatioRowsWithScopesData?.uerPriceListRatioRowsWithScopes?.totalCount,
        );
    }, [
        getRowParams,
        uerPriceListRatioRowsWithScopesData?.uerPriceListRatioRowsWithScopes?.items,
        uerPriceListRatioRowsWithScopesData?.uerPriceListRatioRowsWithScopes?.totalCount,
    ]);

    return {
        dataSource,
        uerPriceListRatioRowsWithScopesStatus,
        changeFilterHandler,
    };
};
